import { useEffect, useState } from "react";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";
import {
  needsFrameCount,
  dueDateInDays,
  buildActionDetails,
  FOLLOW_UP_PRESETS,
  alreadyLoggedToday,
} from "../../services/hiveActions";
import { WithdrawalNotice } from "./WithdrawalNotice";

/**
 * Confirmation step for a quick action: how many frames (#185) and whether
 * to schedule a revisit (#170).
 *
 * Quick actions used to fire on a single click with no feedback at all,
 * which was reported as "nothing happens" during testing -- the action was
 * saving fine, but a server error left the list looking untouched and there
 * was no confirmation either way. This dialog replaces that silence for
 * every action, not just the ones needing a count, so the interaction is
 * consistent and a save is always acknowledged.
 *
 * Frame count is required for frame actions because an action recorded
 * without one is the thing that made the original log useless.
 */
export function LogActionDialog({ open, actionType, actionLabel, hive, actions, withdrawal, onCancel, onSubmit }) {
  const wantsFrames = needsFrameCount(actionType);

  /*
   * ALREADY LOGGED TODAY (#375).
   *
   * Reported from a device: six "Replaced Frames" on one hive and nothing
   * asked. That is one person logging the same job repeatedly, and the
   * other entry is already in the timeline this page has loaded.
   *
   * IN THIS DIALOG, not a second one on top of it. Web already stops here
   * before writing -- to capture a frame count and a reminder -- so the
   * warning belongs on the step that exists rather than as an extra
   * click. A confirm stacked on a confirm is two things to dismiss and
   * teaches people to dismiss both.
   *
   * It does not disable "Log it". Working a hive twice in a day is a real
   * thing and the answer may genuinely be yes; this makes sure the answer
   * is given rather than assumed.
   */
  const repeat = open && alreadyLoggedToday(actions, actionType);
  // Holds "" while the box is being cleared to type a fresh number. It used
  // to coerce every keystroke to at least 1, so clearing the field snapped
  // it back to 1 and the next digit APPENDED -- clear, type "4", get 14.
  // The floor is applied where it matters instead: on submit.
  const [frameCount, setFrameCount] = useState(1);
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState(null); // preset key, or null for none
  const [followUpNote, setFollowUpNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset per opening: the dialog instance is reused across actions, and
  // carrying a previous frame count or reminder into the next hive would be
  // a data-integrity bug, not just a cosmetic one.
  useEffect(() => {
    if (open) {
      setFrameCount(1);
      setNote("");
      setFollowUp(null);
      setFollowUpNote("");
      setBusy(false);
    }
  }, [open, actionType]);

  // An empty box means "1", not "none" -- the action happened either way.
  const _frames = Math.min(100, Math.max(1, Number(frameCount) || 1));

  async function handleSubmit() {
    setBusy(true);
    try {
      const preset = FOLLOW_UP_PRESETS.find((p) => p.key === followUp);
      await onSubmit({
        details: buildActionDetails({ note, frameCount: wantsFrames ? _frames : undefined }),
        followUp: preset
          ? { due_date: dueDateInDays(preset.days), description: followUpNote.trim() || null }
          : null,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={actionLabel || "Log action"}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={busy}>
            {busy ? "Saving…" : "Log it"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "var(--space-4)" }}>
        {/* Honey that must not go in a jar yet (#404). ABOVE the repeat
            warning and in danger rather than warning: a repeat is "did
            you mean to do this twice", this is "the honey you are about
            to take cannot legally be sold yet". If both fire, this is
            the one that matters. */}
        <WithdrawalNotice withdrawal={withdrawal} hiveId={hive?.hive_id} actionType={actionType} />
        {repeat && (
          <p
            role="status"
            style={{
              margin: 0,
              padding: "var(--space-2) var(--space-3)",
              borderLeft: "3px solid var(--color-warning)",
              background: "var(--color-warning-subtle, transparent)",
              color: "var(--color-text)",
            }}
          >
            <strong>Already logged today.</strong>{" "}
            {actionLabel || actionType} is already on this hive for today. Log it
            again only if you really did it twice.
          </p>
        )}
        {hive?.site_name && (
          <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
            {hive.site_name} — Hive {hive.hive_number}
          </p>
        )}

        {wantsFrames && (
          <div>
            <label htmlFor="frame-count" style={{ display: "block", marginBottom: "var(--space-2)" }}>
              How many frames?
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
              <Button
                variant="secondary"
                type="button"
                aria-label="Fewer frames"
                onClick={() => setFrameCount(Math.max(1, _frames - 1))}
              >
                −
              </Button>
              <input
                id="frame-count"
                type="number"
                min="1"
                max="100"
                value={frameCount}
                onChange={(e) => setFrameCount(e.target.value === "" ? "" : Number(e.target.value))}
                style={{ width: "5rem", textAlign: "center" }}
              />
              <Button
                variant="secondary"
                type="button"
                aria-label="More frames"
                onClick={() => setFrameCount(Math.min(100, _frames + 1))}
              >
                +
              </Button>
            </div>
          </div>
        )}

        <div>
          <label htmlFor="action-note" style={{ display: "block", marginBottom: "var(--space-2)" }}>
            Note (optional)
          </label>
          <textarea
            id="action-note"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>

        <fieldset style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-3)" }}>
          <legend style={{ padding: "0 var(--space-2)" }}>Come back to this hive?</legend>
          <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
            <Button
              type="button"
              variant={followUp === null ? "primary" : "secondary"}
              size="sm"
              onClick={() => setFollowUp(null)}
            >
              No reminder
            </Button>
            {FOLLOW_UP_PRESETS.map((p) => (
              <Button
                key={p.key}
                type="button"
                size="sm"
                variant={followUp === p.key ? "primary" : "secondary"}
                onClick={() => setFollowUp(p.key)}
              >
                {p.label}
              </Button>
            ))}
          </div>
          {followUp && (
            <div style={{ marginTop: "var(--space-3)" }}>
              <label htmlFor="follow-up-note" style={{ display: "block", marginBottom: "var(--space-2)" }}>
                Why? (optional)
              </label>
              <input
                id="follow-up-note"
                type="text"
                value={followUpNote}
                onChange={(e) => setFollowUpNote(e.target.value)}
                placeholder="e.g. check if the queen is laying"
                style={{ width: "100%" }}
              />
            </div>
          )}
        </fieldset>
      </div>
    </Modal>
  );
}
